import { ApiFilterOperation, ResultDto, TableColumn } from '@eas/common-web';
import {
  DEADLINES_IDENTIFIER,
  DEADLINES_VERSION,
} from '@modules/deadlines/deadlines';
import {
  UNPROCESSED_DELIVERED_DOCUMENTS_IDENTIFIER,
  UNPROCESSED_DELIVERED_DOCUMENTS_VERSION,
} from '@modules/document-delivered-unprocessed/unprocessed-documents';
import {
  ALL_ES_FILES_IDENTIFIER,
  ALL_ES_FILES_VERSION,
} from '@modules/es-file-all/all-es-files';
import {
  COURT_FILES_JUDGE_OFFICE_IDENTIFIER,
  COURT_FILES_JUDGE_OFFICE_VERSION,
} from '@modules/es-file-court/judge-office/court-files-judge-office';
import { TASK_IDENTIFIER, TASK_VERSION } from '@modules/task/tasks';
import { Me, UserRole } from '@models';
import {
  DeadlineState,
  EvidenceApiUrl,
  EvidenceBrowserUrl,
  Role,
  TaskState,
} from '@enums';
import {
  createInitialFilterState,
  fetchJudgeAutocomplete,
  fetchUserRoleAutocomplete,
  getCurrentUserJudge,
  getFilterStateIndexFromColumnsByKey,
} from '../utils';
import { JudgeFileCount } from './use-court-file-counts';

type UserOverviewStatisticsKey =
  | 'activeFilesAccordingToAssistants'
  | 'courtFilesWithRunningDeadline'
  | 'unfinishedTasksForUser'
  | 'unprocessedDeliveredDocuments';

type UserOverviewStatistics = Record<
  UserOverviewStatisticsKey,
  | { id?: string; value?: number | string }[]
  | { value?: number | string }
  | undefined
>;

type UserOverviewRole =
  | Role.JUDGE
  | Role.INDEX
  | Role.LUSTRUM
  | Role.ASSISTANT
  | 'Default';

type UserOverviewItemDefinition = {
  key: string;
  label: string;
  statisticsKey?: UserOverviewStatisticsKey;
  onClick: () => void;
};

export type UserOverviewItem = {
  key: string;
  label: string;
  value?: number | string;
  onClick: () => void;
};

type UseUserOverviewItemsByRolesParams = {
  user?: Me;
  statistics: UserOverviewStatistics;
  taskColumns: TableColumn<any>[];
  allEsFilesColumns: TableColumn<any>[];
  unprocessedDocumentsColumns: TableColumn<any>[];
  deadlineColumns: TableColumn<any>[];
  courtFilesJudgeOfficeColumns: TableColumn<any>[];
  judgeFileCount?: number;
  assistantFileCount?: number;
  assistantJudges: JudgeFileCount[];
  assistantRole?: UserRole;
  getTableSettings: (settingsId: string, version: number) => any;
  setTableSettings: (settingsId: string, settings: any) => void;
  navigate: (url: EvidenceBrowserUrl) => void;
};

const setFilterStateByColumnKey = (
  filtersState: any[],
  columns: TableColumn<any>[],
  columnKey: string,
  filterState: any
) => {
  const index = getFilterStateIndexFromColumnsByKey(columns, columnKey);

  if (index === -1) {
    return false;
  }

  filtersState[index] = filterState;
  return true;
};

const USER_OVERVIEW_ROLE_PRIORITY = [Role.JUDGE, Role.INDEX, Role.LUSTRUM];

const getUserOverviewRole = (user?: Me): UserOverviewRole => {
  const userRoles = user?.userRoles?.map((userRole) => userRole.role) ?? [];

  return (USER_OVERVIEW_ROLE_PRIORITY.find((role) =>
    userRoles.includes(role)
  ) ??
    (userRoles.includes(Role.ASSISTANT)
      ? Role.ASSISTANT
      : 'Default')) as UserOverviewRole;
};

const getCurrentUserId = (user?: Me) => {
  return (
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id ??
    user?.id ??
    ''
  );
};

const getCurrentIndexUserRole = (user?: Me) => {
  return user?.userRoles?.find((userRole) => userRole.role === Role.INDEX);
};

const getCurrentAssistantUserRole = (user?: Me) =>
  user?.userRoles?.find((userRole) => userRole.role === Role.ASSISTANT);

const getCurrentUserName = (user?: Me) =>
  user?.additionalDetails?.activeSubstitution?.substitutedUser?.name ??
  user?.name;

export async function fetchAssistantUserRole(user?: Me) {
  const substitutedUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;

  if (!substitutedUserId) {
    return getCurrentAssistantUserRole(user);
  }

  try {
    const response = await fetch(`${EvidenceApiUrl.USERS}/role/autocomplete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filters: [
          {
            field: 'user.id',
            value: substitutedUserId,
            operation: ApiFilterOperation.EQ,
          },
          {
            field: 'role.id',
            value: Role.ASSISTANT,
            operation: ApiFilterOperation.EQ,
          },
        ],
      }),
    });
    const result = (await response.json()) as ResultDto<any>;
    return result?.items?.[0];
  } catch {
    return undefined;
  }
}

async function fetchUserAutocomplete(user?: Me) {
  let userObject = {
    id: getCurrentUserId(user),
    label: user?.name ?? '',
    active: true,
  };

  try {
    const response = await fetch(`${EvidenceApiUrl.USERS}/autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filters: [
          {
            field: 'id',
            value: getCurrentUserId(user),
            operation: ApiFilterOperation.EQ,
          },
        ],
      }),
    });
    const result = (await response.json()) as ResultDto<any>;
    if (result?.items?.[0]) {
      userObject = result.items[0] as any;
    }
    return userObject;
  } catch (error) {
    console.log(error);
  }
  return userObject;
}

export function useUserOverviewItemsByRoles({
  user,
  statistics,
  taskColumns,
  allEsFilesColumns,
  unprocessedDocumentsColumns,
  deadlineColumns,
  courtFilesJudgeOfficeColumns,
  judgeFileCount,
  assistantFileCount,
  assistantJudges,
  assistantRole,
  getTableSettings,
  setTableSettings,
  navigate,
}: UseUserOverviewItemsByRolesParams): UserOverviewItem[] {
  const openUnprocessedDocuments = () => {
    const settingsId = UNPROCESSED_DELIVERED_DOCUMENTS_IDENTIFIER + '_TABLE';
    const version = UNPROCESSED_DELIVERED_DOCUMENTS_VERSION;
    const settings = getTableSettings(settingsId, version);
    const filtersState = createInitialFilterState(unprocessedDocumentsColumns);

    setTableSettings(settingsId, {
      ...settings,
      version,
      filtersState,
      searchQuery: '',
    });
    navigate(EvidenceBrowserUrl.DOCUMENTS_DELIVERED_UNPROCESSED);
  };

  const openUnfinishedTasksForUser = async () => {
    const settingsId = TASK_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(settingsId, TASK_VERSION);
    const filtersState = createInitialFilterState(taskColumns);

    const hasStateFilter = setFilterStateByColumnKey(
      filtersState,
      taskColumns,
      'state',
      {
        enabled: true,
        value: TaskState.SOLVING,
        operation: ApiFilterOperation.EQ,
      }
    );

    const userObject = await fetchUserAutocomplete(user);

    const hasSolverFilter = setFilterStateByColumnKey(
      filtersState,
      taskColumns,
      'solver',
      {
        enabled: true,
        value: getCurrentUserId(user),
        operation: ApiFilterOperation.EQ,
        object: userObject,
      }
    );

    if (!hasStateFilter || !hasSolverFilter) {
      return;
    }

    setTableSettings(settingsId, {
      ...settings,
      version: TASK_VERSION,
      searchQuery: '',
      filtersState,
    });

    navigate(EvidenceBrowserUrl.TASKS);
  };

  const openDefaultRunningDeadlines = () => {
    const settingsId = ALL_ES_FILES_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(settingsId, ALL_ES_FILES_VERSION);
    const filtersState = createInitialFilterState(allEsFilesColumns);

    const hasRunningDeadlineFilter = setFilterStateByColumnKey(
      filtersState,
      allEsFilesColumns,
      'documents.deadlines',
      {
        enabled: true,
        value: DeadlineState.RUNNING,
        operation: ApiFilterOperation.EQ,
      }
    );

    if (!hasRunningDeadlineFilter) {
      return;
    }

    setTableSettings(settingsId, {
      ...settings,
      version: ALL_ES_FILES_VERSION,
      searchQuery: '',
      filtersState,
    });

    navigate(EvidenceBrowserUrl.ES_FILES_ALL);
  };

  const openIndexRunningDeadlines = async () => {
    const settingsId = DEADLINES_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(settingsId, DEADLINES_VERSION);
    const filtersState = createInitialFilterState(deadlineColumns);

    const indexUserRole = getCurrentIndexUserRole(user);

    if (!indexUserRole?.role || !indexUserRole?.id) {
      return;
    }

    const hasStateFilter = setFilterStateByColumnKey(
      filtersState,
      deadlineColumns,
      'state',
      {
        enabled: true,
        value: DeadlineState.RUNNING,
        operation: ApiFilterOperation.EQ,
      }
    );

    const hasIndexFilter = setFilterStateByColumnKey(
      filtersState,
      deadlineColumns,
      'document.esFile.indexUserRole.id',
      {
        enabled: true,
        value: indexUserRole?.id ?? '',
        operation: ApiFilterOperation.EQ,
        object: await fetchUserRoleAutocomplete({
          id: indexUserRole?.id,
          role: Role.INDEX,
        }),
      }
    );

    if (!hasStateFilter || !hasIndexFilter) {
      return;
    }

    setTableSettings(settingsId, {
      ...settings,
      version: DEADLINES_VERSION,
      searchQuery: '',
      filtersState,
    });

    navigate(EvidenceBrowserUrl.DEADLINES);
  };

  const openJudgeFilesToResolve = async () => {
    const settingsId = COURT_FILES_JUDGE_OFFICE_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(
      settingsId,
      COURT_FILES_JUDGE_OFFICE_VERSION
    );
    const filtersState = createInitialFilterState(courtFilesJudgeOfficeColumns);
    const judge = getCurrentUserJudge(user);

    if (!judge.id) {
      return;
    }

    const judgeObject = await fetchJudgeAutocomplete(judge);

    const hasJudgeFilter = setFilterStateByColumnKey(
      filtersState,
      courtFilesJudgeOfficeColumns,
      'judge',
      {
        enabled: true,
        value: judge.id ?? '',
        operation: ApiFilterOperation.EQ,
        object: judgeObject,
      }
    );

    if (!hasJudgeFilter) {
      return;
    }

    setTableSettings(settingsId, {
      ...settings,
      version: COURT_FILES_JUDGE_OFFICE_VERSION,
      searchQuery: '',
      filtersState,
    });

    navigate(EvidenceBrowserUrl.COURT_FILES_JUDGE_OFFICE);
  };

  const openAssistantFiles = async () => {
    if (!assistantRole?.id) {
      return;
    }
    const settingsId = COURT_FILES_JUDGE_OFFICE_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(
      settingsId,
      COURT_FILES_JUDGE_OFFICE_VERSION
    );
    const filtersState = createInitialFilterState(courtFilesJudgeOfficeColumns);
    const hasAssistantFilter = setFilterStateByColumnKey(
      filtersState,
      courtFilesJudgeOfficeColumns,
      'assistantUserRoles',
      {
        enabled: true,
        value: assistantRole.id,
        operation: ApiFilterOperation.EQ,
        object: await fetchUserRoleAutocomplete({
          id: assistantRole.id,
          role: Role.ASSISTANT,
        }),
      }
    );

    if (!hasAssistantFilter) {
      return;
    }

    setTableSettings(settingsId, {
      ...settings,
      version: COURT_FILES_JUDGE_OFFICE_VERSION,
      searchQuery: '',
      filtersState,
    });

    navigate(EvidenceBrowserUrl.COURT_FILES_JUDGE_OFFICE);
  };

  const openAssistantJudgeFiles = async (judge: JudgeFileCount) => {
    const settingsId = COURT_FILES_JUDGE_OFFICE_IDENTIFIER + '_TABLE';
    const settings = getTableSettings(
      settingsId,
      COURT_FILES_JUDGE_OFFICE_VERSION
    );
    const filtersState = createInitialFilterState(courtFilesJudgeOfficeColumns);
    if (
      !setFilterStateByColumnKey(
        filtersState,
        courtFilesJudgeOfficeColumns,
        'judge',
        {
          enabled: true,
          value: judge.id,
          operation: ApiFilterOperation.EQ,
          object: await fetchJudgeAutocomplete(judge),
        }
      )
    )
      return;

    setTableSettings(settingsId, {
      ...settings,
      version: COURT_FILES_JUDGE_OFFICE_VERSION,
      searchQuery: '',
      filtersState,
    });
    navigate(EvidenceBrowserUrl.COURT_FILES_JUDGE_OFFICE);
  };

  const commonItems: Record<string, UserOverviewItemDefinition> = {
    unprocessedDocuments: {
      key: 'unprocessedDocuments',
      label: 'Nezpracované dokumenty',
      statisticsKey: 'unprocessedDeliveredDocuments',
      onClick: openUnprocessedDocuments,
    },
    unfinishedTasks: {
      key: 'unfinishedTasks',
      label: 'Nedokončené úlohy',
      statisticsKey: 'unfinishedTasksForUser',
      onClick: openUnfinishedTasksForUser,
    },
  };

  const itemDefinitionsByRole: Record<
    UserOverviewRole,
    UserOverviewItemDefinition[]
  > = {
    [Role.LUSTRUM]: [
      commonItems.unprocessedDocuments,
      commonItems.unfinishedTasks,
    ],
    [Role.INDEX]: [
      commonItems.unprocessedDocuments,
      commonItems.unfinishedTasks,
      {
        key: 'indexRunningDeadlines',
        label: 'Spisy s běžící lhůtou',
        statisticsKey: 'courtFilesWithRunningDeadline',
        onClick: openIndexRunningDeadlines,
      },
    ],
    [Role.JUDGE]: [
      {
        key: 'judgeFilesToResolve',
        label: 'Moje spisy k vyřízení',
        onClick: openJudgeFilesToResolve,
      },
      commonItems.unfinishedTasks,
    ],
    [Role.ASSISTANT]: [
      {
        key: 'assistantFilesToResolve',
        label: 'Moje spisy k vyřízení',
        statisticsKey: 'activeFilesAccordingToAssistants',
        onClick: openAssistantFiles,
      },
      ...assistantJudges.map((judge) => ({
        key: `assistantJudgeFiles-${judge.id}`,
        label: `Spisy soudce ${judge.name}`,
        statisticsKey: 'activeFilesAccordingToAssistants' as const,
        onClick: () => openAssistantJudgeFiles(judge),
      })),
      commonItems.unfinishedTasks,
    ],
    Default: [
      commonItems.unprocessedDocuments,
      commonItems.unfinishedTasks,
      {
        key: 'defaultRunningDeadlines',
        label: 'Spisy s běžící lhůtou',
        statisticsKey: 'courtFilesWithRunningDeadline',
        onClick: openDefaultRunningDeadlines,
      },
    ],
  };

  const userOverviewItems = itemDefinitionsByRole[
    getUserOverviewRole(user)
  ].map((item) => {
    const stat = item.statisticsKey
      ? statistics[item.statisticsKey]
      : undefined;

    return {
      key: item.key,
      label: item.label,
      value:
        item.key === 'judgeFilesToResolve'
          ? judgeFileCount
          : item.key === 'assistantFilesToResolve'
          ? assistantFileCount
          : item.key.startsWith('assistantJudgeFiles-')
          ? assistantJudges.find(
              (judge) => `assistantJudgeFiles-${judge.id}` === item.key
            )?.value
          : Array.isArray(stat)
          ? stat.find((entry) => entry.id === getCurrentUserName(user))?.value
          : stat?.value,
      onClick: item.onClick,
    };
  });

  return userOverviewItems;
}
